import { AlertTriangle, FileCheck } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { TableSkeleton } from "~/components/ui/skeletons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
// Import custom components and hooks
import ClientRiskEditorModal from "~/pages/risk/components/ClientRiskEditorModal";
import ClientRiskTable from "~/pages/risk/components/ClientRiskTable";
import EmptyClientState from "~/pages/risk/components/EmptyClientState";
import { useClientRisk } from "~/pages/risk/hooks/useClientRisk";

const ClientRiskManagementPage = () => {
  const {
    pendingClients,
    processedClients,
    loading,
    editingClient,
    insuredAmount,
    trailingPaymentDays,
    salesConditionId,
    activeTab,
    setActiveTab,
    setInsuredAmount,
    setTrailingPaymentDays,
    setSalesConditionId,
    startEditing,
    cancelEditing,
    saveClientRiskData,
  } = useClientRisk();

  return (
    <div className="flex max-w-[90vw] flex-col gap-4 sm:px-10 md:max-w-[70vw]">
      <h1 className="text-2xl font-bold">Gestión de Riesgo de Clientes</h1>

      <Card>
        <CardHeader>
          <CardTitle>Evaluación de Riesgo y Condiciones de Venta</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-4 py-4"
          >
            <TabsList className="grid h-14 w-full grid-cols-2">
              <TabsTrigger
                value="pending"
                className="flex min-h-[3rem] items-center gap-2 sm:min-h-fit"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="whitespace-normal text-sm leading-tight sm:whitespace-nowrap sm:leading-normal">
                  Pendientes de Evaluación
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="processed"
                className="flex items-center gap-2"
              >
                <FileCheck className="h-4 w-4" /> Evaluados
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="space-y-4">
              {loading ? (
                <TableSkeleton rows={6} columns={8} />
              ) : pendingClients.length === 0 ? (
                <EmptyClientState isPending={true} />
              ) : (
                <ClientRiskTable
                  clients={pendingClients}
                  showActions={true}
                  onEditClient={startEditing}
                  isLoading={loading}
                />
              )}
            </TabsContent>

            <TabsContent value="processed" className="space-y-4">
              {loading ? (
                <TableSkeleton rows={6} columns={8} />
              ) : processedClients.length === 0 ? (
                <EmptyClientState isPending={false} />
              ) : (
                <ClientRiskTable
                  clients={processedClients}
                  showActions={true}
                  onEditClient={startEditing}
                  isLoading={loading}
                />
              )}
            </TabsContent>
          </Tabs>

          {editingClient && (
            <ClientRiskEditorModal
              client={editingClient}
              insuredAmount={insuredAmount}
              trailingPaymentDays={trailingPaymentDays}
              salesConditionId={salesConditionId}
              setInsuredAmount={setInsuredAmount}
              setTrailingPaymentDays={setTrailingPaymentDays}
              setSalesConditionId={setSalesConditionId}
              onSave={saveClientRiskData}
              onCancel={cancelEditing}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientRiskManagementPage;
